import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, UserPlus, MapPin, ShieldCheck, ShieldAlert, Eye, EyeOff, Pencil, Trash2, X } from "lucide-react";
import { getPriests, createPriest, updatePriest, deletePriest, importPriests, apiErr } from "../lib/api";

const EMPTY = { name: "", email: "", phone: "", areas: "", specialties: "", languages: "", experience_years: "", starting_price: "" };

function PriestForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const body = {
      name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null,
      areas: f.areas ? f.areas.split(",").map((s) => s.trim()).filter(Boolean) : [],
      specialties: f.specialties ? f.specialties.split(",").map((s) => s.trim()).filter(Boolean) : [],
      languages: f.languages ? f.languages.split(",").map((s) => s.trim()).filter(Boolean) : [],
      experience_years: f.experience_years ? Number(f.experience_years) : null,
      starting_price: f.starting_price ? Number(f.starting_price) : null,
    };
    try {
      if (f.id) await updatePriest(f.id, body);
      else await createPriest(body);
      toast.success(f.id ? "Priest updated" : "Priest added");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="priest-form-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-semibold text-[#2C241B]">{f.id ? "Edit purohit" : "Add a purohit"}</h3>
          <button onClick={onClose} className="rounded-md p-1.5 text-[#9A9082] hover:bg-[#F5F1E8]"><X size={18} /></button>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[["name","Full name","text"],["email","Email","email"],["phone","Phone","text"],["experience_years","Experience (years)","number"],["starting_price","Starting price (₹)","number"]].map(([k,label,type]) => (
            <div key={k} className={k==="name"?"sm:col-span-2":""}>
              <label className="font-body text-sm font-medium text-[#2C241B]">{label}</label>
              <input type={type} value={f[k]} onChange={set(k)} data-testid={`priest-field-${k}`}
                className="mt-1 h-10 w-full rounded-lg border border-[#E5DFD3] px-3 font-body text-sm outline-none focus:border-[#E0A98F]" />
            </div>
          ))}
          {[["areas","Areas (comma separated)"],["specialties","Puja specialties (comma separated)"],["languages","Languages (comma separated)"]].map(([k,label]) => (
            <div key={k} className="sm:col-span-2">
              <label className="font-body text-sm font-medium text-[#2C241B]">{label}</label>
              <input value={f[k]} onChange={set(k)} data-testid={`priest-field-${k}`}
                className="mt-1 h-10 w-full rounded-lg border border-[#E5DFD3] px-3 font-body text-sm outline-none focus:border-[#E0A98F]" />
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-[#E5DFD3] px-4 py-2 font-body text-sm font-medium text-[#5C5449] hover:bg-[#F5F1E8]">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="priest-form-save"
            className="rounded-lg bg-[#D84315] px-4 py-2 font-body text-sm font-semibold text-white hover:bg-[#BF360C] disabled:opacity-60">
            {busy ? "Saving…" : (f.id ? "Save changes" : "Create purohit account")}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).filter(Boolean).map((line) => {
    const cells = line.split(",");
    const row = {};
    headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
    return {
      name: row.name || "", email: row.email || null, phone: row.phone || null,
      areas: (row.areas || "").split(";").map((s) => s.trim()).filter(Boolean),
      specialties: (row.specialties || "").split(";").map((s) => s.trim()).filter(Boolean),
      languages: (row.languages || "").split(";").map((s) => s.trim()).filter(Boolean),
      experience_years: row.experience_years ? Number(row.experience_years) : null,
      starting_price: row.starting_price ? Number(row.starting_price) : null,
    };
  }).filter((r) => r.name);
}

export default function Priests() {
  const [priests, setPriests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = () => getPriests().then(setPriests).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiErr(e)); } };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) { toast.error("No valid rows found (need a 'name' column)"); return; }
    try { const r = await importPriests(rows); toast.success(`Imported: ${r.created} created, ${r.updated} updated`); load(); }
    catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-priests">
      <p className="font-body text-[12px] font-semibold uppercase tracking-[0.14em] text-[#C1440E]">Provider operations</p>
      <h1 className="mt-1 font-heading text-3xl font-semibold text-[#2C241B] md:text-[38px]">Priest quality and coverage</h1>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#8A8072]">Verify applications, maintain public marketplace profiles, and control the areas where each priest can be discovered.</p>

      {/* Bulk import */}
      <div className="mt-7 rounded-2xl border border-[#E9E2D6] bg-white p-6" data-testid="priest-bulk-import">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FBEDE7]"><UploadCloud size={17} className="text-[#D84315]" /></span>
          <div className="flex-1">
            <p className="font-body text-[11px] font-semibold uppercase tracking-[0.12em] text-[#A79C8A]">High-volume onboarding</p>
            <h2 className="mt-0.5 font-heading text-xl font-semibold text-[#2C241B]">Import up to 250 Purohits</h2>
            <p className="mt-1 font-body text-sm text-[#8A8072]">Upload a CSV to create managed marketplace profiles. Columns: <code className="rounded bg-[#F5F1E8] px-1 text-xs">name, email, phone, areas, specialties, languages</code> (use <b>;</b> inside list fields). Existing emails are updated, not duplicated.</p>
            <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[#E5DFD3] bg-[#FAFAF5] px-4 py-2 font-body text-sm font-medium text-[#5C5449] hover:bg-[#F5F1E8]">
              <UploadCloud size={15} /> Choose CSV file
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onCsv} data-testid="priest-csv-input" />
            </label>
            <div className="mt-5 grid grid-cols-1 gap-4 border-t border-[#F0EBE1] pt-4 sm:grid-cols-3">
              {[["No email sent","Managed profiles are created silently."],["Duplicate-aware","Existing priest records are updated."],["Row-level results","Valid rows continue if another row fails."]].map(([t,d]) => (
                <div key={t}><p className="font-body text-sm font-semibold text-[#2C241B]">{t}</p><p className="font-body text-xs text-[#8A8072]">{d}</p></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Manual add */}
      <button onClick={() => setForm(EMPTY)} data-testid="priest-add-manual"
        className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-[#E9E2D6] bg-white p-5 text-left transition-colors hover:border-[#E0A98F]">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FBEDE7]"><UserPlus size={17} className="text-[#D84315]" /></span>
        <div><p className="font-heading text-base font-semibold text-[#C1440E]">Add one Purohit manually</p><p className="font-body text-xs text-[#8A8072]">Create a managed profile directly. No invitation or email is sent.</p></div>
      </button>

      {/* List */}
      <div className="mt-6 flex items-center justify-between">
        <p className="font-body text-sm font-semibold text-[#2C241B]" data-testid="priest-count">{loading ? "Loading…" : `${priests.length} purohits`}</p>
      </div>

      <div className="mt-3 space-y-3">
        {priests.map((p) => (
          <div key={p.id} data-testid={`priest-row-${p.id}`} className="flex flex-col gap-4 rounded-2xl border border-[#E9E2D6] bg-white p-4 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FBEDE7] font-body text-sm font-semibold text-[#D84315]">
                {p.name.slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-base font-semibold text-[#2C241B]">{p.name}</span>
                  {p.verified
                    ? <span className="rounded-full bg-[#E6F0EE] px-2 py-0.5 font-body text-[11px] font-semibold text-[#2F7A6B]">Verified</span>
                    : <span className="rounded-full bg-[#FBF3E7] px-2 py-0.5 font-body text-[11px] font-semibold text-[#B98A2E]">Unverified</span>}
                  {p.hidden && <span className="rounded-full bg-[#F3EEEA] px-2 py-0.5 font-body text-[11px] font-semibold text-[#8A8072]">Hidden from marketplace</span>}
                </div>
                <p className="mt-1 flex items-center gap-1 font-body text-xs text-[#8A8072]"><MapPin size={12} /> {p.areas?.join(", ") || "No areas set"}</p>
                <p className="mt-1 font-body text-xs text-[#A79C8A]">{p.specialties?.join(" · ") || "No specialties"}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!p.verified && (
                <button onClick={() => act(() => updatePriest(p.id, { verified: true }), "Priest verified")} data-testid={`priest-verify-${p.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#E6F0EE] px-3 py-1.5 font-body text-xs font-semibold text-[#2F7A6B] hover:bg-[#d9e9e5]"><ShieldCheck size={14} /> Verify</button>
              )}
              {p.verified && (
                <button onClick={() => act(() => updatePriest(p.id, { verified: false }), "Verification removed")} data-testid={`priest-unverify-${p.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#E5DFD3] px-3 py-1.5 font-body text-xs font-medium text-[#8A8072] hover:bg-[#F5F1E8]"><ShieldAlert size={14} /> Unverify</button>
              )}
              <button onClick={() => act(() => updatePriest(p.id, { hidden: !p.hidden }), p.hidden ? "Shown in marketplace" : "Hidden from marketplace")} data-testid={`priest-hide-${p.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E5DFD3] px-3 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]">
                {p.hidden ? <><Eye size={14} /> Show</> : <><EyeOff size={14} /> Hide</>}
              </button>
              <button onClick={() => setForm({ ...EMPTY, ...p, areas: (p.areas||[]).join(", "), specialties: (p.specialties||[]).join(", "), languages: (p.languages||[]).join(", "), experience_years: p.experience_years ?? "", starting_price: p.starting_price ?? "" })}
                data-testid={`priest-edit-${p.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E5DFD3] px-3 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]"><Pencil size={14} /> Edit</button>
              <button onClick={() => { if (window.confirm(`Remove ${p.name}?`)) act(() => deletePriest(p.id), "Priest removed"); }} data-testid={`priest-delete-${p.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#FDECEA] px-3 py-1.5 font-body text-xs font-semibold text-[#C62828] hover:bg-[#f8dcd8]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!loading && priests.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E9E2D6] bg-white px-6 py-16 text-center font-body text-sm text-[#A79C8A]">No purohits yet — add one manually or import a CSV.</div>
        )}
      </div>

      {form && <PriestForm initial={form} onClose={() => setForm(null)} onSaved={load} />}
    </div>
  );
}
